/**
 * Presenter actions — the simulated EHR / patient / clinician events and the
 * real mutations some of them need. Each action: mutate → refetch → dispatch,
 * idempotent where the script fires it once, errors to the presenter log. No
 * clock control: every event happens now. Nothing here is undone one change at
 * a time: the undo is Reset database, which reseeds the tenant.
 */
import { ownerFor } from "../engine/assignment";
import { evalAuthLine } from "../engine/authLedger";
import { currentBranchClock } from "../components/branchClock";
import { addDaysToKey, addWorkingDaysToKey, dateKeyAt, dateKeyRange, selectDemoNow, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { availableWeekdays, parseWindow, planConflictItems } from "../engine/constraints";
import { evaluateMove } from "../engine/moveGuard";
import { gatesClear, initialGates } from "../engine/gates";
import { createHold, holdWindowEnd, type HoldSlot } from "../engine/holds";
import { expectedPlanFor, planTemplate } from "../engine/expectedPlan";
import { territoryForZip } from "@caredna-marlin/utils/homeHealthDemo/engine/territory";
import { certEndDate, expandTrack, startVersion } from "@caredna-marlin/utils/homeHealthDemo/engine/planExpansion";
import { holdSlotFor, openStartOnDay, selectReferralChecks, unassignedFallbackSlot, type SocCandidate } from "../engine/referralChecks";
import { visitTypeLabel } from "../engine/roster";
import { OKAFOR_WELCOME_CALL } from "@caredna-marlin/utils/homeHealthDemo/fixtures/patients";
import { CALL_OUT_DAY_OFFSET } from "@caredna-marlin/utils/homeHealthDemo/fixtures/cast";
import { effectiveOrders } from "../engine/holds";
import { getDemoStore } from "../store/instance";
import { resetAsOf } from "../engine/tenantDemoDay";
import { freshSeed } from "../store/instance";
import type { DateKey, DemoState, Discipline, Episode, FrequencyOrder, Hold, IsoString, PatientConstraint, PlanVersion, ReferralPlanChoice } from "@caredna-marlin/utils/homeHealthDemo/state";
import { PatientSexEnum } from "~/graphql/__generated__/graphql";
import { CalendarBlockEventStatusEnum, createPatient, moveCalendarBlock, moveVisitResource, refetchAfterMutation, refetchAfterReset, resetDemoTenant, scheduleOverrideVisit, setCalendarBlockStatus, setShiftStatus, ShiftStatusEnum, updateVisitStatus, type DemoClient, type DriveMinutes } from "../data/mutations";
import { emptyDriveTimePlan, planDriveTimesForMoves, type DriveTimePlan, type PlannedBlockMove, type PlannedMove } from "../data/driveTimes";
import type { CreateCalendarBlockMutationVariables } from "~/graphql/__generated__/graphql";
import type { OrgConfig } from "~/providers/OrgConfigProvider";
import { roundDateFor, roundFor, roundSendTime, scriptedReplies, selectConfirmationRows } from "../engine/confirmationRound";
import { type SequenceChange } from "../engine/sequenceDay";

let client: DemoClient | undefined;

export function attachApolloClient(c: DemoClient): void {
  client = c;
}

function requireClient(): DemoClient {
  if (!client) throw new Error("Presenter actions need the Apollo client (is the demo layer mounted?)");
  return client;
}

/**
 * The organization's visit-resource requirements and shift centres, which the
 * command centre's drive-time recompute reads. Handed over by
 * `HomeHealthDemoRoot` once its query has resolved, the way the client is.
 */
let orgConfig: OrgConfig | undefined;

export function attachOrgConfig(c: OrgConfig): void {
  orgConfig = c;
}

/**
 * How long a move waits for the day to be priced before it goes without new
 * drive minutes. Well inside `LOCAL_OVERLAY_TTL_MS` (60s), so the optimistic
 * card is never dropped for a slow Google call; the move itself still lands.
 */
export const DRIVE_TIME_TIMEOUT_MS = 12_000;

function withTimeout<T>(work: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s`)), ms);
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/**
 * Price the day the way the command centre does, and never let that stop a move.
 *
 * Nothing on the server recomputes drive time when a visit moves, so the minutes
 * ride along with the move. If the paid call is refused or
 * slow the move still goes: the rows keep the minutes they had, which is stale
 * rather than invented, and the presenter log says so. On screen the segments
 * either side of the move stay dashed and the gap reads *recomputing* until the
 * horizon poll lands, exactly as they do now — the poll clears that state
 * whether or not the recompute succeeded, so nothing is stuck there.
 */
async function planDriveTimes(moves: PlannedMove[], blockMoves: PlannedBlockMove[] = []): Promise<DriveTimePlan> {
  if (moves.length === 0 && blockMoves.length === 0) return emptyDriveTimePlan();
  if (!client || !orgConfig || orgConfig.loading) {
    log("Drive times not recomputed: the organization's configuration is not loaded yet", "warn");
    return emptyDriveTimePlan();
  }
  try {
    return await withTimeout(planDriveTimesForMoves(client, orgConfig, tz(), moves, blockMoves), DRIVE_TIME_TIMEOUT_MS, "Drive-time recompute");
  } catch (error) {
    log(`Drive times not recomputed (${error instanceof Error ? error.message : String(error)}) — the day keeps the minutes it had`, "warn");
    return emptyDriveTimePlan();
  }
}

/**
 * 3.2 Price a **staged** day — nothing is written (Pete, 2026-09-19: *"I still
 * want the drive times updated each drop and I'd like them to look the same as
 * drive times from visits that haven't been moved"*).
 *
 * `GetSimpleDriveTime` is a **query**. Pricing a day costs a paid Google call and
 * writes nothing, so My day can ask what a proposed arrangement would drive the
 * moment a card lands and draw the answer as an ordinary number, while the writes
 * still wait for **Save**. The plan that comes back is the same one `Save` would
 * have computed for that arrangement, so Save takes it rather than paying twice.
 *
 * A refusal or a timeout is already handled the way M2 chose: `planDriveTimes`
 * logs the warning and answers with an empty plan, and the caller keeps the
 * minutes the day already had — stale rather than invented.
 */
export async function priceStagedDay(moves: PlannedMove[], blockMoves: PlannedBlockMove[] = []): Promise<DriveTimePlan> {
  return planDriveTimes(moves, blockMoves);
}

/** The neighbours a reprice changed: drive minutes only, so no visit's schedule is touched. */
async function writeNeighbourDriveTimes(c: DemoClient, drive: Record<number, DriveMinutes>, movedIds: number[]): Promise<void> {
  for (const [id, minutes] of Object.entries(drive)) {
    if (movedIds.includes(Number(id))) continue;
    await moveVisitResource(c, { id: Number(id), drive: minutes });
  }
}

const store = () => getDemoStore();
const state = () => store().getState();
const dispatch = (e: Parameters<ReturnType<typeof getDemoStore>["dispatch"]>[0]) => store().dispatch(e);
const nowIso = (): IsoString => selectDemoNow(state(), new Date()).toISOString();

export function log(text: string, level: "info" | "warn" | "error" = "info"): void {
  dispatch({ type: "PRESENTER_LOG", line: { atIso: nowIso(), level, text } });
}

function fired(key: string): boolean {
  return state().presenter.fired.includes(key);
}

function markFired(key: string): void {
  dispatch({ type: "PRESENTER_FIRED", key });
}

async function guarded(label: string, fn: () => Promise<void> | void): Promise<void> {
  try {
    await fn();
  } catch (error) {
    log(`${label} failed: ${error instanceof Error ? error.message : String(error)}`, "error");
    throw error;
  }
}

function tz(): string {
  return state().config.branch.timeZone;
}

/**
 * The clock the presenter log writes on. A log line is written by a store action
 * rather than rendered by a component, so the preference is read straight off the
 * same localStorage key the provider reads. A line already in the log is a stored
 * string, so flipping the switch re-phrases the screens and leaves the log's
 * history as it was written.
 */
function logClock() {
  return currentBranchClock(tz());
}

function today(): string {
  return toDateKey(selectDemoNow(state(), new Date()), tz());
}

function requireBinding<T>(value: T | undefined, what: string): T {
  if (value === undefined) throw new Error(`${what} is not bound to the tenant yet — is the tenant seeded and the horizon loaded?`);
  return value;
}

// ---------------------------------------------------------------------------
// Act 1 — referral accept
// ---------------------------------------------------------------------------

export interface KeyedReferralInput {
  firstName: string;
  lastName: string;
  addressStreet: string;
  addressLineTwo?: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
  /**
   * Google's own id for the place, when the address came from the product's
   * autocomplete. The server prefers it over re-geocoding the text, so a picked
   * address lands on the coordinates the branch saw rather than on whatever a
   * second lookup makes of the same string.
   */
  addressPlaceId?: string;
  sex?: PatientSexEnum;
  dateOfBirth?: string;
  payer: "MA" | "FFS";
  source: string;
  expectedDischarge: DateKey;
  /** A plan template key; its orders become the referral's. */
  caseType: string;
}

/**
 * **A referral typed in by hand** (Pete, 2026-09-24), for a prospect whose own
 * referrals arrive by phone or fax rather than through an interface.
 *
 * The patient is created **for real**, through the product's own `CreatePatient`
 * — which geocodes the address on the way in, and that is the point: without
 * coordinates the scheduler cannot place anybody near them, and the referral
 * panel would have nothing to offer. Everything after that is the demo's, and
 * the referral arrives already on the queue, because somebody typing it in is
 * the arrival.
 */
export async function addKeyedReferral(input: KeyedReferralInput): Promise<string> {
  const s = state();
  const ref = `keyed-${Date.now().toString(36)}`;
  const orders = planTemplate(s.config, input.caseType)?.orders ?? [];
  let patientId = 0;
  await guarded("Add referral", async () => {
    const created = await createPatient(requireClient(), {
      firstName: input.firstName,
      lastName: input.lastName,
      addressStreet: input.addressStreet,
      addressLineTwo: input.addressLineTwo ?? null,
      addressCity: input.addressCity,
      addressState: input.addressState,
      addressZip: input.addressZip,
      addressPlaceId: input.addressPlaceId ?? null,
      phone: null,
      sex: input.sex ?? null,
      dateOfBirth: input.dateOfBirth || null,
      patientNotes: null,
      serviceInstructions: null,
      patientIdentifiers: [],
    });
    patientId = created.id;
    // A keyed-in patient stays in the tenant until the next Reset database, which is
    // the honest cost of creating one for real.
    await refetchAfterMutation(requireClient());
  });
  const displayName = `${input.firstName.charAt(0).toUpperCase()}. ${input.lastName}`;
  dispatch({
    type: "REFERRAL_KEYED",
    serverPatientId: patientId,
    patient: {
      ref,
      displayName,
      firstName: input.firstName,
      lastName: input.lastName,
      payer: input.payer,
      zip: input.addressZip,
      territory: territoryForZip(s.config.branch, input.addressZip),
      constraints: [],
    },
    referral: {
      id: ref,
      patientRef: ref,
      source: input.source,
      receivedAtIso: nowIso(),
      orders,
      caseType: input.caseType,
      status: "new",
      expectedDischarge: input.expectedDischarge,
      /**
       * **The plan somebody picked, as their choice.** The orders copied off the
       * template carry frequencies, which is exactly what a referral arriving
       * *with orders on file* looks like — so without this the panel read the
       * plan back as "Orders on file" rather than the template that was chosen
       * in the form (Pete, 2026-09-24). Recording the choice makes the dropdown
       * open on it, and leaves it changeable like any other referral's.
       */
      expectedPlan: { kind: "template", templateKey: input.caseType },
    },
  });
  log(`Referral keyed in: ${displayName} from ${input.source}`);
  return ref;
}

/**
 * **The branch declines a referral.** Nothing has been promised yet, so this
 * gives nothing back — the referral simply leaves the queue. The panel was
 * written without one deliberately (a failed check is a reason on the screen and
 * nothing blocks); it is here because a referral can now be keyed in by hand and
 * they need a way off the list again (Pete, 2026-09-24).
 */
export function declineReferral(referralId: string): void {
  const s = state();
  const referral = s.referrals[referralId];
  if (!referral || referral.status !== "new") return;
  dispatch({ type: "REFERRAL_DECLINED", referralId });
  log(`Declined ${s.patients[referral.patientRef]?.displayName ?? referralId}`);
}

/** 1.2 The referral reaches the queue: intake had it, now the branch does. */
export function referralArrived(referralId: string): void {
  const referral = state().referrals[referralId];
  if (!referral || referral.status !== "pending") return;
  dispatch({ type: "REFERRAL_ARRIVED", referralId, nowIso: nowIso() });
  log(`Referral arrived: ${state().patients[referral.patientRef]?.displayName ?? referralId}`);
}

/**
 * A reservation that yielded: the start of care has forty-eight hours and the
 * recertification has five days, so the looser one re-places itself and the
 * log says where it went. Read off the state either side of the dispatch,
 * so the line can never disagree with what the week draws.
 */
function logYieldedReservations(before: DemoState["planVisits"], after: DemoState["planVisits"]): void {
  const s = state();
  for (const [id, next] of Object.entries(after)) {
    const prev = before[id];
    if (!prev || prev.windowStart === undefined) continue;
    if (prev.targetDate === next.targetDate && prev.windowStart === next.windowStart) continue;
    const episode = s.episodes[next.episodeId];
    const name = episode ? s.patients[episode.patientRef]?.displayName ?? episode.patientRef : next.episodeId;
    const what = next.rule === "recert" ? "recertification" : visitTypeLabel(s.config, next.visitTypeKind).toLowerCase();
    log(next.windowStart ? `${name}: ${what} moved to ${next.targetDate} ${logClock().wall(next.windowStart)} to make room` : `${name}: ${what} gave up its time and needs a new one — no free span inside its window`);
  }
}

/** 1.2 Accept and place hold → the hold lands (assigned SOC placeholder + the tail) and the patient joins Pending admissions. Always enabled: with no candidate the hold is unassigned on the due date. */
/**
 * `preferHandle` is the SOC option somebody picked in the panel. Without it the
 * search's own first answer stands, which is what every caller before the
 * options became pickable relied on (Pete, 2026-09-22).
 */
/**
 * The option somebody chose on the panel, **as the panel had it**. It has to
 * travel: the panel's options come from the product's scheduler, and this
 * function's own reading of them comes from the demo's search, which is exactly
 * the disagreement the panel was changed to end. Handing back only a clinician's
 * name meant re-deriving their hour here — and when the local search had nothing
 * for them, the hold landed unassigned under a room that had just been shown a
 * time (Pete, 2026-09-24).
 */
export interface PickedSocSlot {
  handle: string;
  date: DateKey;
  time: string;
}

export async function acceptReferral(referralId: string, preferred?: PickedSocSlot): Promise<Hold | undefined> {
  const s = state();
  const referral = s.referrals[referralId];
  if (!referral || referral.status !== "new") return undefined;
  const patient = s.patients[referral.patientRef];
  // One fallback, shared with the panel's prospective hold, so the number the
  // panel showed and the hold Accept places can never come from different days.
  let slot = holdSlotFor(s, referral);
  if (preferred) {
    slot = { providerHandle: preferred.handle, date: preferred.date, windowStart: preferred.time, windowEnd: holdWindowEnd(preferred.time, s.config) };
  }
  if (!slot) {
    slot = unassignedFallbackSlot(s, referral);
    log(`No SOC-capable slot for ${patient?.displayName} inside 48 hours of discharge; hold placed unassigned on ${slot.date}`, "warn");
  }
  const hold = createHold(referral, slot, s.config);
  // Consent/POA is on file at acceptance, as it is for every seeded hold: there
  // is no `consent-alvarez` event and nothing else flips the gate, so an MA hold
  // accepted here would otherwise never be able to convert.
  const before = s.planVisits;
  dispatch({ type: "REFERRAL_ACCEPTED", referralId, hold, readiness: initialGates(s.config, patient?.payer ?? "FFS", { ordersOnFile: true, consentOnFile: true }) });
  log(`Accepted ${patient?.displayName}: SOC hold ${slot.providerHandle ? `on ${s.roster[slot.providerHandle]?.displayName} ` : ""}${slot.date} ${logClock().wall(slot.windowStart)}`);
  logYieldedReservations(before, state().planVisits);
  return hold;
}

/**
 * 1.2 The branch chooses the expected plan: a template, Custom, or back to the
 * orders on file. One selector, so the tail, the panel's capacity row and what
 * the hold reserves all move together. `undefined` puts the suggestion back.
 */
export function setReferralPlan(referralId: string, plan?: ReferralPlanChoice): void {
  const s = state();
  const referral = s.referrals[referralId];
  if (!referral) return;
  dispatch({ type: "REFERRAL_PLAN_SET", referralId, plan });
  const next = expectedPlanFor(state().config, state().referrals[referralId]);
  log(`${s.patients[referral.patientRef]?.displayName ?? referralId}: expected plan set to ${next.label}`);
}

/** 1.3 Intake reports a new discharge date. The row flags; the hold has not moved yet. */
export function dischargeDateChanged(referralId: string, expectedDischarge?: DateKey): void {
  const s = state();
  const referral = s.referrals[referralId];
  if (!referral) return;
  // One working day: intake moves a discharge to the next day the hospital discharges on, never a weekend.
  const next = expectedDischarge ?? addWorkingDaysToKey(referral.expectedDischarge, 1);
  dispatch({ type: "REFERRAL_DISCHARGE_CHANGED", referralId, expectedDischarge: next, nowIso: nowIso() });
  log(`${s.patients[referral.patientRef]?.displayName}: discharge date changed to ${next}`);
}

/** 1.3 Update D/C Date: the hold moves to the first open slot in the new window; the day it left goes back into the read. */
export function reHold(holdId: string, expectedDischarge?: DateKey): HoldSlot | undefined {
  const s = state();
  const hold = s.holds[holdId];
  const referral = hold ? s.referrals[hold.referralId] : undefined;
  if (!hold || !referral || hold.status !== "held") return undefined;
  const next = expectedDischarge ?? referral.expectedDischarge;
  if (next !== referral.expectedDischarge) dispatch({ type: "REFERRAL_DISCHARGE_CHANGED", referralId: referral.id, expectedDischarge: next, nowIso: nowIso() });
  const slot = holdSlotFor(state(), state().referrals[referral.id]);
  if (!slot) {
    log(`No SOC-capable slot for ${s.patients[hold.patientRef]?.displayName} inside 48 hours of ${next}; the hold stays on ${hold.date}`, "warn");
    return undefined;
  }
  const from = hold.date;
  const before = state().planVisits;
  dispatch({ type: "HOLD_MOVED", holdId, slot });
  log(`${s.patients[hold.patientRef]?.displayName}: start-of-care hold moved from ${from} to ${slot.date} ${logClock().wall(slot.windowStart)}${slot.providerHandle ? ` on ${s.roster[slot.providerHandle]?.displayName}` : ""}`);
  logYieldedReservations(before, state().planVisits);
  return slot;
}

/** 1.3 Release: the slot and the estimate go back to capacity and the row leaves the list. Never silent. */
export function releaseHold(holdId: string): void {
  const s = state();
  const hold = s.holds[holdId];
  if (!hold || hold.status !== "held") return;
  dispatch({ type: "HOLD_RELEASED", holdId });
  log(`${s.patients[hold.patientRef]?.displayName}: hold released — ${hold.date} ${logClock().wall(hold.windowStart)} back in the read`);
}

/** 1.4 The welcome-call wizard completes: structured constraints land, readiness flips; FFS holds convert. */
export async function welcomeCallCompleted(patientRef: string, constraints?: PatientConstraint[]): Promise<void> {
  const answers = constraints ?? (patientRef === "okafor" ? OKAFOR_WELCOME_CALL : []);
  dispatch({ type: "WELCOME_CALL_COMPLETED", patientRef, constraints: answers, nowIso: nowIso() });
  const conflicts = planConflictItems(state(), patientRef, nowIso());
  if (conflicts.length) dispatch({ type: "ROWS_UPSERT", items: conflicts });
  log(`Welcome call complete for ${state().patients[patientRef]?.displayName}: ${answers.length} constraints captured${conflicts.length ? `; ${conflicts.length} planned visit(s) conflict → worklist` : ""}`);
  await convertReadyHolds(patientRef);
}

/** 2.3.2 Constraints edited after admission: same fields, same cascade, no gate to flip. */
export function patientConstraintsUpdated(patientRef: string, constraints: PatientConstraint[]): void {
  dispatch({ type: "PATIENT_CONSTRAINTS_SET", patientRef, constraints });
  const conflicts = planConflictItems(state(), patientRef, nowIso());
  if (conflicts.length) dispatch({ type: "ROWS_UPSERT", items: conflicts });
  log(`Constraints updated for ${state().patients[patientRef]?.displayName}: ${constraints.length} constraints${conflicts.length ? `; ${conflicts.length} planned visit(s) conflict → worklist` : ""}`);
}

async function convertReadyHolds(patientRef: string): Promise<void> {
  for (const hold of Object.values(state().holds)) {
    if (hold.patientRef !== patientRef || hold.status !== "held" || !gatesClear(state().readiness[hold.id] ?? {})) continue;
    await convertSoc(hold.id);
  }
}

/** 2.4.3 Conversion: the SOC hold becomes a real visit on the clinician's real calendar. */
export async function convertSoc(holdId: string): Promise<number | undefined> {
  const s = state();
  const hold = s.holds[holdId];
  if (!hold || hold.status !== "held") return hold?.convertedVisitId;
  const patient = s.patients[hold.patientRef];
  const b = s.meta.bindings;
  const patientId = requireBinding(b.patientIdsByRef[hold.patientRef], `patient ${hold.patientRef}`);
  const providerId = requireBinding(hold.providerHandle ? b.providerIdsByHandle[hold.providerHandle] : undefined, `clinician ${hold.providerHandle}`);
  const visitTypeId = requireBinding(b.visitTypeIdsByKind.SOC, "SOC visit type");
  const serviceLineId = requireBinding(b.serviceLineId, "service line");
  const duration = s.config.visitTypes.SOC.plannedMinutes;
  // The welcome call captured when the patient is available; the start of care lands inside that
  // window rather than at the hold's hour. Nothing is flagged: it is placed, not scheduled over.
  const placed = snapIntoWindow(hold.windowStart, patient?.constraints ?? []);
  // …and then past whatever is already on that clinician's day. The hold itself
  // is excluded: it is the thing being converted, and it reserved this span
  // precisely so the start of care could land in it. The search is sized on the
  // visit alone for the same reason — the drive allowance is already reserved.
  const start = openStartOnDay(s, providerId, hold.date, placed.start, { excludeHoldIds: [hold.id] }) ?? placed.start;
  const startTime = dateKeyAt(hold.date, start, tz()).toISOString();
  let realVisitId = 0;
  await guarded("Convert SOC hold", async () => {
    const created = await scheduleOverrideVisit(requireClient(), {
      patientId,
      serviceLineId,
      organizationalUnitId: null,
      visitTypeId,
      startTime,
      resources: [{ providerId, duration, offset: 0, resourceRequirementId: null, shiftId: null }],
      serviceIds: [],
      duration,
      reschedulingVisitId: null,
      serviceInstructions: null,
      note: null,
    });
    realVisitId = created.id;
    dispatch({ type: "VISIT_CREATED_BY_DEMO", visitId: realVisitId });
    await refetchAfterMutation(requireClient());
  });
  const socDate = hold.date;
  const episode = {
    id: `ep-${hold.patientRef}`,
    patientRef: hold.patientRef,
    socDate,
    certEndDate: certEndDate(socDate, s.config),
    versions: [],
    authLedger: patient?.payer === "MA" ? [evalAuthLine("RN", socDate)] : [],
    lupaThreshold: s.config.lupaThresholdPerPeriod,
    pendingDisciplines: [],
  };
  dispatch({
    type: "HOLD_CONVERTED",
    holdId,
    realVisitId,
    episode,
    annotation: {
      realVisitId,
      holdId,
      origin: "soc",
      confirmation: "notSent",
      lastSeenProviderId: providerId,
      lastSeenStartIso: startTime,
      placedInsideWindow: placed.window ? { holdStart: hold.windowStart, placedStart: start, window: placed.window } : undefined,
    },
  });
  log(`${patient?.displayName}: SOC hold converted → real visit ${realVisitId} on ${s.roster[hold.providerHandle!]?.displayName} ${hold.date} ${start}`);
  return realVisitId;
}

/**
 * Conversion places the start of care inside the window the welcome call captured:
 * the later of the hold's hour and the window's start. Outside a hard window the
 * hold's hour stands.
 */
export function snapIntoWindow(holdStart: string, constraints: PatientConstraint[]): { start: string; window?: string } {
  const c = constraints.find((x) => x.kind === "availabilityWindow");
  const bounds = c ? parseWindow(c.value) : undefined;
  if (!c || !bounds) return { start: holdStart };
  const [h, m] = holdStart.split(":").map(Number);
  const minutes = Math.max(h * 60 + m, bounds[0]);
  if (minutes === h * 60 + m) return { start: holdStart };
  return { start: `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`, window: c.value };
}

// ---------------------------------------------------------------------------
// Act 2 — the plan of care becomes the week
// ---------------------------------------------------------------------------

function episodeForPatient(s: DemoState, patientRef: string): Episode | undefined {
  return Object.values(s.episodes)
    .filter((e) => e.patientRef === patientRef)
    .sort((a, b) => b.socDate.localeCompare(a.socDate))[0];
}

function ordersWith(previous: PlanVersion | undefined, order: FrequencyOrder): FrequencyOrder[] {
  return [...(previous?.orders ?? []).filter((o) => o.discipline !== order.discipline), order];
}

/**
 * 2.2 Therapy lands. A discipline evaluates first and plans after, so its orders
 * arrive days after the start of care: the track is anchored on the day that
 * discipline evaluated, plotted onto the clinician who owns the patient's
 * territory for that discipline, and the discipline leaves the branch read's estimate in the same
 * dispatch.
 */
export function ptOrdersArrive(patientRef: string, pattern = "2w4", discipline: Discipline = "PT"): void {
  const s = state();
  const episode = episodeForPatient(s, patientRef);
  if (!episode) return;
  const key = `track:${episode.id}:${discipline}`;
  if (fired(key)) return;
  const previous = episode.versions[episode.versions.length - 1];
  const order: FrequencyOrder = { discipline, pattern };
  const version = startVersion(previous, ordersWith(previous, order), nowIso(), `${discipline} orders synced from the EHR`);
  const existing = Object.values(s.planVisits).filter((v) => v.episodeId === episode.id);
  const delivered = existing.find((v) => v.discipline === discipline && v.visitTypeKind.endsWith("_EVAL"));
  const evalDelivered = !!delivered;
  const handle = ownerFor(s, s.patients[patientRef]?.territory, discipline) ?? existing.find((v) => v.discipline === discipline)?.providerHandle;
  // The track is anchored on the day the discipline actually evaluated, not on today:
  // anchoring on today puts the second visit of a twice-weekly week two days later, which
  // on a Wednesday is a Saturday. The eval's week is also the first week the estimate
  // covers, so the four plotted weeks line up with the four estimated ones.
  const anchorDate = delivered?.targetDate ?? today();
  // The days the welcome call ruled out stay out, as the start of care's own plan keeps them out.
  const planVisits = expandTrack(episode, order, anchorDate, version.version, s.config, { evalDelivered, availableWeekdays: availableWeekdays(s.patients[patientRef]) }).map((v) => ({ ...v, providerHandle: handle }));
  dispatch({ type: "PLAN_TRACK_ADDED", episodeId: episode.id, version, planVisits, removePending: discipline });
  markFired(key);
  log(`${s.patients[patientRef]?.displayName}: ${discipline} orders ${pattern} — ${planVisits.length} visit(s) planned onto ${s.roster[handle ?? ""]?.displayName ?? "the territory owner"} from ${anchorDate}`);
}

// ---------------------------------------------------------------------------
// Act 3 — the clinician's week
// ---------------------------------------------------------------------------

function providerIdForHandle(handle: string): number {
  return requireBinding(state().meta.bindings.providerIdsByHandle[handle], `clinician ${handle}`);
}

/**
 * 3.3 **The round goes.** One text per real visit on the clinician's next
 * working day whose patient has text consent, carrying the time the schedule
 * already has. Nothing was drafted and nothing was reviewed.
 */
export function fireConfirmationRound(handle: string, forDate?: DateKey): number {
  const providerId = providerIdForHandle(handle);
  const date = forDate ?? roundDateFor(state(), new Date());
  const before = selectConfirmationRows(state(), providerId, date).filter((r) => r.status === "notSent").length;
  dispatch({ type: "ROUND_FIRED", providerId, forDate: date, nowIso: nowIso() });
  const after = selectConfirmationRows(state(), providerId, date);
  const pending = after.filter((r) => r.status === "pending").length;
  const noConsent = after.filter((r) => r.reasons.some((x) => x.kind === "noConsent")).length;
  log(`${state().roster[handle]?.displayName}: ${pending} text${pending === 1 ? "" : "s"} went for ${date} via ${state().config.confirmationChannel}${noConsent ? `; ${noConsent} without text consent` : ""}${before === 0 ? " (nothing was waiting)" : ""}`);
  return pending;
}

/** 3.3 **Delay.** This clinician's round today goes back an hour, up to two. The caption, and nothing else. */
export function delayConfirmationRound(handle: string, forDate?: DateKey): void {
  const providerId = providerIdForHandle(handle);
  const date = forDate ?? roundDateFor(state(), new Date());
  dispatch({ type: "ROUND_DELAYED", providerId, forDate: date });
  log(`${state().roster[handle]?.displayName}: texts go at ${logClock().wall(roundSendTime(state(), roundFor(state(), providerId, date)))}`);
}

/**
 * 3.3 **The cutoff.** The replies and the cutoff land together: every pending row
 * says yes except the one the script names, who asks for the afternoon, and the
 * last routine row of the day, which never answers.
 */
export function confirmationReplies(handle = "dana", asksLater = "Whitfield", words = "afternoon?", forDate?: DateKey, declines = "Blackwell"): number {
  const providerId = providerIdForHandle(handle);
  const date = forDate ?? roundDateFor(state(), new Date());
  const rows = selectConfirmationRows(state(), providerId, date);
  /**
   * The 3.3 beat includes one patient who says no outright, named rather than
   * positional because the first row of Dana's Thursday is Halvorsen and the Say
   * line leans on him being confirmed (script author, turn 71).
   *
   * **Blackwell, not Osei** (Pete and script author, turn 75). Osei is a Thursday
   * *and* Friday patient, so a refused Thursday has nowhere inside the week to go
   * — the next open day is Monday and the guardrail reads *Frequency this week 1
   * of 2*, which is true but is not an answer the room wants. Blackwell is
   * Tuesday and Thursday with Friday free: the rebook lands inside the week, the
   * guardrail reads 2 of 2, and the beat answers the question it raises.
   *
   * A caller who names nobody gets the round's ordinary behaviour.
   */
  const replies = scriptedReplies(rows, asksLater, words, declines);
  if (replies.length === 0) {
    log("Nothing has been texted yet — fire the round first", "warn");
    return 0;
  }
  dispatch({ type: "ROUND_REPLIES", providerId, forDate: date, replies, nowIso: nowIso() });
  const after = selectConfirmationRows(state(), providerId, date);
  log(`${date}: ${after.filter((r) => r.status === "confirmed").length} confirmed, ${after.filter((r) => r.status === "needsCall").length} need a call`);
  return replies.length;
}

/** 3.3 The clinician called the patient and confirmed it herself. No second text goes out. */
export function confirmByPhoneNow(realVisitId: number): void {
  dispatch({ type: "VISIT_CONFIRMED_BY_PHONE", realVisitId, nowIso: nowIso() });
  const name = state().server.visitResources.find((v) => v.visitId === realVisitId)?.patientName;
  if (name) log(`${name}: confirmed by phone`);
}

/**
 * 3.2 A block on the clinician's own day is created by the product's own
 * `AddCalendarBlock` control, which already holds the mutation. This records the
 * block's address, so the card carries its address line — the horizon read does
 * not return it.
 */
export async function recordBlockCreated(blockId: number, block: CreateCalendarBlockMutationVariables): Promise<void> {
  const s = state();
  const startIso = new Date(block.startTime).toISOString();
  const endIso = new Date(block.endTime).toISOString();
  const address = [block.addressStreet, block.addressCity].filter(Boolean).join(", ") || undefined;
  if (address) dispatch({ type: "BLOCK_ADDRESS_RECORDED", blockId, address });
  const handle = Object.entries(s.meta.bindings.providerIdsByHandle).find(([, id]) => id === block.providerId)?.[0];
  log(`${handle ? s.roster[handle]?.displayName : `provider ${block.providerId}`}: ${block.title ?? "block"} ${toDateKey(new Date(startIso), tz())}${address ? ` · ${address}` : ""}`);
  if (client) await refetchAfterMutation(client);
}

/**
 * 3.2 The proposed order is accepted: one real move per visit that changed slot,
 * then one refetch. The whole day is one shift, so the reprice is one call for
 * the batch however many visits moved — and it has to happen, because the dialog
 * has just put a drive total on screen that the day would otherwise not have.
 */
export async function acceptSequence(changes: SequenceChange[]): Promise<number> {
  if (changes.length === 0) return 0;
  const applied = nowIso();
  for (const change of changes) {
    const vr = state().server.visitResources.find((v) => v.visitId === change.visitId);
    if (!vr) continue;
    dispatch({ type: "LOCAL_VISIT_MOVED", change: { visitId: change.visitId, providerId: vr.providerId, startIso: change.toIso, endIso: change.toEndIso, appliedAtIso: applied } });
  }
  const moves: PlannedMove[] = [];
  for (const change of changes) {
    const vr = state().server.visitResources.find((v) => v.visitId === change.visitId);
    if (!vr) continue;
    moves.push({ visitResourceId: vr.id, fromProviderId: vr.providerId, fromStartIso: vr.startIso, fromEndIso: vr.endIso, toStartIso: change.toIso, toEndIso: change.toEndIso });
  }
  const plan = await planDriveTimes(moves);
  const movedIds = moves.map((move) => move.visitResourceId);
  try {
    await guarded("Sequence day", async () => {
      const c = requireClient();
      for (const change of changes) {
        const vr = state().server.visitResources.find((v) => v.visitId === change.visitId);
        if (!vr) continue;
        await moveVisitResource(c, { id: vr.id, startIso: change.toIso, endIso: change.toEndIso, drive: plan.next[vr.id] });
      }
      await writeNeighbourDriveTimes(c, plan.next, movedIds);
      await refetchAfterMutation(c);
    });
  } catch (error) {
    dispatch({ type: "LOCAL_ROLLBACK", keys: changes.map((c) => `visit:${c.visitId}`) });
    throw error;
  }
  log(`Day re-sequenced: ${changes.length} visit${changes.length === 1 ? "" : "s"} moved`);
  return changes.length;
}

/**
 * 3.2 / 4.2 A real visit moves on My week. The card lands first and the server
 * catches up; a refusal puts it back where it was and says why.
 */
export async function moveVisitOptimistically(realVisitId: number, to: { handle?: string; startIso: string; endIso: string }): Promise<void> {
  const s = state();
  const vr = s.server.visitResources.find((v) => v.visitId === realVisitId);
  if (!vr) throw new Error(`Visit ${realVisitId} is not in the horizon`);
  const providerId = to.handle ? requireBinding(s.meta.bindings.providerIdsByHandle[to.handle], `clinician ${to.handle}`) : vr.providerId;
  const reservationsBefore = s.planVisits;
  // On My week the drop is the commit, so re-placement runs here rather than on
  // a Save that never comes.
  dispatch({ type: "LOCAL_VISIT_MOVED", change: { visitId: realVisitId, providerId, startIso: to.startIso, endIso: to.endIso, appliedAtIso: nowIso() } });
  logYieldedReservations(reservationsBefore, state().planVisits);
  try {
    await moveVisit(realVisitId, { handle: to.handle, startIso: to.startIso, endIso: to.endIso });
  } catch (error) {
    dispatch({ type: "LOCAL_ROLLBACK", keys: [`visit:${realVisitId}`] });
    throw error;
  }
}

/**
 * 3.2 A visit is canceled from My week's card menu by the product's own
 * `CancelVisitDialog` — the same dialog the command center's visit popup opens,
 * which holds the reason list and the mutation. This records the result: the
 * card comes off the day without waiting for the horizon poll. Its derived
 * **Canceled, rebook** row is wanted while the demo runs; Reset database is what
 * clears it, by reseeding the visit.
 *
 * The reason is not recorded: the product dialog does not report which one was
 * chosen, and nothing here invents one; the reason lives on the visit's record,
 * which is where the command center's popup reads it from.
 */
export async function recordVisitCanceled(realVisitId: number): Promise<void> {
  const s = state();
  const vr = s.server.visitResources.find((v) => v.visitId === realVisitId);
  dispatch({ type: "LOCAL_VISIT_CANCELED", visitId: realVisitId, appliedAtIso: nowIso() });
  log(vr ? `${vr.patientName}: ${toDateKey(new Date(vr.startIso), tz())} visit canceled` : `Visit ${realVisitId} canceled`);
  if (client) await refetchAfterMutation(client);
}

/**
 * 3.2 A block is re-timed on My day's axis. Pete, 2026-09-18: *"Blocks should be
 * draggable just like visits."*
 *
 * The same shape as a visit move — the card lands where she dropped it and the
 * server catches up; a refusal puts it back and says why — with two differences,
 * both of them the product's own:
 *
 * - **No drive reprice here.** A visit move has to price the day in the browser
 *   and send the minutes with it (M2), because nothing on the server recomputes
 *   drive when a visit resource is written. A calendar block is the opposite
 *   case: `services/calendarBlock.ts` reprices the whole shift in the same
 *   transaction as the write. Pricing it again on this side would be a second,
 *   paid Google call for an answer the server already has.
 * - **No guardrail evaluation.** There is no patient behind a block, so there is
 *   no window, frequency or period to check. The only rule a block drop obeys is
 *   My day's own: nothing lands on another stop's span.
 */
export async function moveBlockOptimistically(blockId: number, to: { startIso: string; endIso: string }): Promise<void> {
  const s = state();
  const block = s.server.blocks.find((b) => b.id === blockId);
  if (!block) throw new Error(`Block ${blockId} is not in the horizon`);
  dispatch({ type: "LOCAL_BLOCK_MOVED", change: { blockId, startIso: to.startIso, endIso: to.endIso, appliedAtIso: nowIso() } });
  try {
    await guarded("Move block", async () => {
      const c = requireClient();
      await moveCalendarBlock(c, { id: blockId, startIso: to.startIso, endIso: to.endIso });
      await refetchAfterMutation(c);
    });
  } catch (error) {
    dispatch({ type: "LOCAL_ROLLBACK", keys: [`moveBlock:${blockId}`] });
    throw error;
  }
  log(`${block.title ?? `Block ${blockId}`} moved to ${logClock().dateTime(to.startIso)}`);
}

/**
 * One re-time staged on My day's axis and waiting for **Save**: a visit or a
 * block, and the hours it is to land on.
 */
export interface StagedRetime {
  kind: "visit" | "block";
  /** The visit being re-timed; `blockId` carries the block instead. */
  realVisitId?: number;
  blockId?: number;
  startIso: string;
  endIso: string;
}

/**
 * 3.2 **Save** on My day: every staged re-time written at once (Pete,
 * 2026-09-19: *"I'd like there to be one save button that saves all pending
 * changes instead of it happening on each drop"*).
 *
 * A drop on the axis used to be a write. It is now a proposal the panel holds in
 * its own memory, and this is the write — the same mutations and the same drive
 * reprice as a drag ever made.
 *
 * Three things about the batch:
 *
 * - **The day is usually not priced here at all.** My day prices each staged
 *   arrangement as it is made (`priceStagedDay`) so the drive reads as an
 *   ordinary number before Save, and hands that plan back here: the same answer
 *   Save would have computed, already paid for. `priced` is that plan. Only when
 *   it is missing — the panel's pricing was refused, or a caller that does not
 *   price at all — does Save pay for the day itself, and then
 *   `planDriveTimesForMoves` deduplicates the shifts so the whole batch is one
 *   paid `GetSimpleDriveTime`.
 * - **The visits go first and the blocks last.** `services/calendarBlock.ts`
 *   reprices the whole shift in the same transaction as a block write, so a block
 *   written after the visits prices the day the visits left behind, and its
 *   server-side answer is the day's last word on drive.
 * - **The overlay is laid down before the first `await`**, the way
 *   `acceptSequence` does it: the panel drops its staged changes the moment this
 *   resolves, and a card that had only ever been staged would otherwise snap back
 *   to its server hour until the horizon poll landed.
 *
 * A refusal rolls the overlay back and rethrows; the panel keeps the staged
 * changes so they can be tried again.
 */
export async function saveDayRetimes(changes: StagedRetime[], priced?: DriveTimePlan): Promise<number> {
  const s = state();
  const visits = changes.filter((c) => c.kind === "visit" && c.realVisitId !== undefined && s.server.visitResources.some((v) => v.visitId === c.realVisitId));
  // A block the horizon has stopped carrying is not written: there is nothing
  // to say where it was.
  const blocks = changes.filter((c) => c.kind === "block" && c.blockId !== undefined && s.server.blocks.some((b) => b.id === c.blockId));
  const skipped = changes.length - visits.length - blocks.length;
  if (skipped > 0) log(`${skipped} staged change${skipped === 1 ? "" : "s"} skipped: the horizon no longer carries what ${skipped === 1 ? "it moves" : "they move"}`, "warn");
  if (visits.length === 0 && blocks.length === 0) return 0;

  const applied = nowIso();
  const keys: string[] = [];
  const reservationsBefore = s.planVisits;
  for (const change of visits) {
    const vr = s.server.visitResources.find((v) => v.visitId === change.realVisitId);
    if (!vr) continue;
    dispatch({ type: "LOCAL_VISIT_MOVED", change: { visitId: vr.visitId, providerId: vr.providerId, startIso: change.startIso, endIso: change.endIso, appliedAtIso: applied } });
    keys.push(`visit:${vr.visitId}`);
  }
  for (const change of blocks) {
    dispatch({ type: "LOCAL_BLOCK_MOVED", change: { blockId: change.blockId!, startIso: change.startIso, endIso: change.endIso, appliedAtIso: applied } });
    keys.push(`moveBlock:${change.blockId}`);
  }
  // The batch is the commit, so this is where a double-booked reservation
  // re-places itself — not under the drag, and not on Reset.
  logYieldedReservations(reservationsBefore, state().planVisits);

  const moves: PlannedMove[] = [];
  for (const change of visits) {
    const vr = s.server.visitResources.find((v) => v.visitId === change.realVisitId);
    if (!vr) continue;
    moves.push({ visitResourceId: vr.id, fromProviderId: vr.providerId, fromStartIso: vr.startIso, fromEndIso: vr.endIso, toStartIso: change.startIso, toEndIso: change.endIso });
  }
  // The panel has already asked what this arrangement drives, and asking again
  // would be a second paid call for an answer we are holding.
  const blockMoves: PlannedBlockMove[] = blocks.map((change) => ({ calendarBlockId: change.blockId!, toStartIso: change.startIso, toEndIso: change.endIso }));
  const plan = priced ?? (await planDriveTimes(moves, blockMoves));
  try {
    const c = requireClient();
    for (const change of visits) {
      const vr = s.server.visitResources.find((v) => v.visitId === change.realVisitId);
      if (!vr) continue;
      await moveVisitResource(c, { id: vr.id, startIso: change.startIso, endIso: change.endIso, drive: plan.next[vr.id] });
    }
    await writeNeighbourDriveTimes(c, plan.next, moves.map((move) => move.visitResourceId));
    for (const change of blocks) {
      const block = s.server.blocks.find((b) => b.id === change.blockId);
      if (!block) continue;
      await moveCalendarBlock(c, { id: change.blockId!, startIso: change.startIso, endIso: change.endIso });
    }
    await refetchAfterMutation(c);
  } catch (error) {
    dispatch({ type: "LOCAL_ROLLBACK", keys });
    log(`My day not saved (${error instanceof Error ? error.message : String(error)}) — whatever landed before the refusal stays; Reset database puts the day back`, "error");
    throw error;
  }
  const count = visits.length + blocks.length;
  log(`My day saved: ${count} change${count === 1 ? "" : "s"}`);
  return count;
}

/**
 * 3.2 A block is removed from My week's card menu. The product has no delete for
 * a block; canceling it is the product's own remove.
 */
export async function removeBlockFromWeek(blockId: number): Promise<void> {
  const s = state();
  const block = s.server.blocks.find((b) => b.id === blockId);
  dispatch({ type: "LOCAL_BLOCK_CANCELED", blockId, appliedAtIso: nowIso() });
  try {
    await guarded("Remove block", async () => {
      const c = requireClient();
      await setCalendarBlockStatus(c, blockId, CalendarBlockEventStatusEnum.Canceled);
      await refetchAfterMutation(c);
    });
  } catch (error) {
    dispatch({ type: "LOCAL_ROLLBACK", keys: [`cancelBlock:${blockId}`] });
    throw error;
  }
  log(`Block removed: ${block?.title ?? `block ${blockId}`}${block ? ` · ${toDateKey(new Date(block.startIso), tz())}` : ""}`);
}

// ---------------------------------------------------------------------------
// Act 5 — the command center
// ---------------------------------------------------------------------------

/** The day 5.2's call-out happens on: the working day after next (`CALL_OUT_DAY_OFFSET`). */
export function callOutDay(s: DemoState = state()): DateKey {
  return addWorkingDaysToKey(s.meta.demoToday, CALL_OUT_DAY_OFFSET);
}

/**
 * 5.2 A call-out: the clinician's shift on the call-out day is canceled for real
 * and the coverage drawer opens. The working day after next, not today, so every
 * visit on it is still ahead of the clock whatever the hour of the demo.
 */
export async function callOutNow(handle: string, day: DateKey = callOutDay()): Promise<void> {
  const s = state();
  if (s.presenter.callOut) return;
  const providerId = requireBinding(s.meta.bindings.providerIdsByHandle[handle], `clinician ${handle}`);
  const shift = s.server.shifts.find((x) => x.providerId === providerId && x.status === "confirmed" && toDateKey(new Date(x.startIso), tz()) === day);
  if (!shift) throw new Error(`${s.roster[handle]?.displayName} has no confirmed shift on ${day}`);
  await guarded("Call-out", async () => {
    await setShiftStatus(requireClient(), shift.id, ShiftStatusEnum.Canceled);
    await refetchAfterMutation(requireClient());
  });
  dispatch({ type: "CALLOUT_OPENED", providerHandle: handle, date: day, startedAtIso: nowIso(), shiftId: shift.id });
  log(`${s.roster[handle]?.displayName} called out for ${day} — shift canceled; the day's visits need cover`);
}

export async function restoreCallOut(): Promise<void> {
  const s = state();
  const c = s.presenter.callOut;
  if (!c) return;
  const shiftId = c.shiftId;
  if (shiftId !== undefined) {
    await guarded("Restore shift", async () => {
      await setShiftStatus(requireClient(), shiftId, ShiftStatusEnum.Confirmed);
      await refetchAfterMutation(requireClient());
    });
  }
  dispatch({ type: "CALLOUT_CLOSED" });
  log(`${s.roster[c.providerHandle]?.displayName}'s shift restored`);
}

/** Rebook a canceled visit within its window on the same clinician's next working day. */
export async function rebookMissed(patientRef: string, opts: { date?: string; time?: string; handle?: string } = {}): Promise<number | undefined> {
  const s = state();
  const planVisit = Object.values(s.planVisits).find((v) => s.episodes[v.episodeId]?.patientRef === patientRef && v.status === "missed");
  if (!planVisit) throw new Error("No canceled visit to rebook");
  const handle = opts.handle ?? planVisit.providerHandle ?? "marcus";
  const providerId = requireBinding(s.meta.bindings.providerIdsByHandle[handle], `clinician ${handle}`);
  const patientId = requireBinding(s.meta.bindings.patientIdsByRef[patientRef], `patient ${patientRef}`);
  const visitTypeId = requireBinding(s.meta.bindings.visitTypeIdsByKind[planVisit.visitTypeKind], `visit type ${planVisit.visitTypeKind}`);
  const serviceLineId = requireBinding(s.meta.bindings.serviceLineId, "service line");
  const candidates = dateKeyRange(addDaysToKey(today(), 1), addDaysToKey(today(), 6));
  const date = opts.date ?? candidates.find((d) => s.server.shifts.some((x) => x.providerId === providerId && x.status === "confirmed" && toDateKey(new Date(x.startIso), tz()) === d)) ?? candidates[0];
  const time = opts.time ?? "15:30";
  const duration = s.config.visitTypes[planVisit.visitTypeKind].plannedMinutes;
  let newId = 0;
  await guarded("Rebook canceled visit", async () => {
    const created = await scheduleOverrideVisit(requireClient(), {
      patientId,
      serviceLineId,
      organizationalUnitId: null,
      visitTypeId,
      startTime: dateKeyAt(date, time, tz()).toISOString(),
      resources: [{ providerId, duration, offset: 0, resourceRequirementId: null, shiftId: null }],
      serviceIds: [],
      duration,
      reschedulingVisitId: planVisit.realVisitId ?? null,
      serviceInstructions: null,
      note: null,
    });
    newId = created.id;
    dispatch({ type: "VISIT_CREATED_BY_DEMO", visitId: newId });
    await refetchAfterMutation(requireClient());
  });
  dispatch({ type: "PLAN_VISIT_UPDATED", planVisit: { ...planVisit, status: "real", realVisitId: newId, targetDate: date, providerHandle: handle } });
  log(`${s.patients[patientRef]?.displayName}: rebooked ${planVisit.visitTypeKind.replace(/_/g, " ").toLowerCase()} on ${s.roster[handle]?.displayName} ${date} ${time}`);
  return newId;
}

/**
 * 3.2 / 4.2 Move a plan visit inside its compliance window, guarded by pace and
 * the period read; a linked real visit moves for real, optimistically, so the
 * command center and My week show the new day at the moment the row's action is
 * confirmed rather than a poll later.
 */
export async function moveWithinWindow(planVisitId: string, toDate: string, toHandle?: string, time?: string): Promise<void> {
  const s = state();
  const ev = evaluateMove(s, planVisitId, toDate, toHandle);
  if (!ev) throw new Error("Plan visit not found");
  if (!ev.allowed) throw new Error(`Move blocked: ${ev.blocks.join("; ")}`);
  const visit = ev.visit;
  const handle = toHandle ?? visit.providerHandle;
  const hhmm = time ?? visit.windowStart ?? "10:00";
  if (visit.realVisitId !== undefined && s.server.visitResources.some((v) => v.visitId === visit.realVisitId)) {
    const start = dateKeyAt(toDate, hhmm, tz());
    const end = new Date(start.getTime() + s.config.visitTypes[visit.visitTypeKind].plannedMinutes * 60_000);
    await moveVisitOptimistically(visit.realVisitId, { handle: handle !== visit.providerHandle ? handle : undefined, startIso: start.toISOString(), endIso: end.toISOString() });
  }
  dispatch({ type: "PLAN_VISIT_UPDATED", planVisit: { ...visit, targetDate: toDate, providerHandle: handle, windowStart: hhmm, windowEnd: visit.windowEnd } });
  log(`${s.patients[s.episodes[visit.episodeId]?.patientRef ?? ""]?.displayName ?? "Patient"}: ${visit.visitTypeKind.replace(/_/g, " ").toLowerCase()} moved to ${toDate}${handle && handle !== visit.providerHandle ? ` on ${s.roster[handle]?.displayName}` : ""} · pace ${ev.paceAfter.weekStatus} / period ${ev.paceAfter.periodStatus}`);
}

/**
 * 2.7.3 / 3.2.1 Move a real visit (clinician and/or time); confirmation resets
 * via the next snapshot.
 *
 * The one place every move funnels through — My week's cross-day drag, My day's
 * within-day re-time and **Move within window** — so it is
 * also the one place the day is repriced. The drive minutes are worked out
 * before the write and sent with it, the way the command centre sends all six
 * fields after a drag, and the neighbours the reprice changed are written too,
 * because drive belongs to the stop it arrives at: the stop after the one that
 * moved, and the day's last stop, are as wrong as the moved visit otherwise.
 */
export async function moveVisit(realVisitId: number, to: { handle?: string; startIso?: string; endIso?: string }): Promise<void> {
  const s = state();
  const vr = s.server.visitResources.find((v) => v.visitId === realVisitId);
  if (!vr) throw new Error(`Visit ${realVisitId} is not in the horizon`);
  const providerId = to.handle ? requireBinding(s.meta.bindings.providerIdsByHandle[to.handle], `clinician ${to.handle}`) : undefined;
  const plan = await planDriveTimes([
    {
      visitResourceId: vr.id,
      fromProviderId: vr.providerId,
      fromStartIso: vr.startIso,
      fromEndIso: vr.endIso,
      toProviderId: providerId,
      toStartIso: to.startIso ?? vr.startIso,
      toEndIso: to.endIso ?? vr.endIso,
    },
  ]);
  await guarded("Move visit", async () => {
    const c = requireClient();
    await moveVisitResource(c, { id: vr.id, providerId, startIso: to.startIso, endIso: to.endIso, drive: plan.next[vr.id] });
    await writeNeighbourDriveTimes(c, plan.next, [vr.id]);
    await refetchAfterMutation(c);
  });
  log(`Visit ${realVisitId} moved${to.handle ? ` to ${s.roster[to.handle]?.displayName}` : ""}${to.startIso ? ` at ${to.startIso}` : ""}`);
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

export function resetStore(): void {
  store().dispatch({ type: "RESET", seed: freshSeed() });
  log("Store reset to seed");
}

/**
 * **Reset database**: replace the tenant's patients, visits, shifts, blocks and
 * availability with the seed for `demoDay`, its visit statuses as of `demoTime`
 * (`HH:mm` in the branch zone), keeping the org, its users and their roles. One request and one transaction on the service: it lands whole or not
 * at all. Every browser's store, this one included, rebuilds itself when the
 * horizon poll reads the new seed stamp.
 */
export async function resetDemo(demoDay: DateKey, demoTime: string, phone: string): Promise<void> {
  const asOf = resetAsOf(demoDay, demoTime, tz()).toISOString();
  log(`Reset database for ${demoDay} at ${demoTime} started`);
  await guarded("Reset database", async () => {
    const c = requireClient();
    await resetDemoTenant(c, { demoDate: demoDay, asOf, phone });
    await refetchAfterReset(c);
  });
  log(`Reset database for ${demoDay} done`);
}

/** The provider id a roster handle is bound to, if the store has bound it. The reset keeps providers, so it outlives one. */
export function boundProviderId(handle: string): number | undefined {
  return state().meta.bindings.providerIdsByHandle[handle];
}

/** Summary counts for the drawer. */
export function selectPresenterStatus(s: DemoState): { holds: number; converted: number; rowsOpen: number; callOut?: string } {
  return {
    holds: Object.values(s.holds).filter((h) => h.status === "held").length,
    converted: Object.values(s.holds).filter((h) => h.status === "converted").length,
    rowsOpen: s.worklistItems.filter((t) => !t.resolved).length,
    callOut: s.presenter.callOut ? s.roster[s.presenter.callOut.providerHandle]?.displayName : undefined,
  };
}

export { updateVisitStatus, effectiveOrders };
